import React, { useRef } from 'react'
import { motion, useInView } from 'framer-motion'
import { Link } from 'react-router-dom'
import { ArrowRight, Check } from 'lucide-react'
import { Navbar, Footer } from '../components/SharedLayout'
import MetaTags from '../components/MetaTags'

const ease = [0.16, 1, 0.3, 1] as const

const colors = {
  teal: '#145F6C',
  tealDark: '#0D3135',
  copper: '#AB9381',
  cream: '#F7F8F6',
  nearBlack: '#0A0B0B',
}

const GHL_BOOKING_URL =
  'https://api.leadconnectorhq.com/widget/booking/vGBU75grNpdx4TI2SvX2'

const BENEFITS = [
  'Locked-in founding member rate of $399/month — guaranteed not to increase',
  'Priority scheduling for all maintenance and service visits',
  'Your own Hudson — assigned exclusively to your home',
  'MyHome Blueprint™ — your personalized five-year home plan',
  'MyHome Vault™ — 24/7 access to your home\'s full service history',
  'Annual supply credit for filters, bulbs, batteries, and more',
  'First access to new services, features, and app launches',
]

const TRUST_ITEMS = [
  '30-Day Satisfaction Guarantee — try it risk-free',
  'Cancel anytime. No contracts, no commitments.',
  'Cassi Luree, Director of Operations, is your direct point of contact.',
]

const BookingCta: React.FC<{
  children: React.ReactNode
  className?: string
}> = ({ children, className = '' }) => (
  <a
    href={GHL_BOOKING_URL}
    target="_blank"
    rel="noopener noreferrer"
    className={`group inline-flex items-center justify-center gap-2 py-3.5 px-8 font-sans font-semibold text-white text-base transition-opacity hover:opacity-90 rounded-none ${className}`}
    style={{ backgroundColor: colors.teal }}
  >
    {children}
    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
  </a>
)

const Hero: React.FC = () => (
  <section
    className="relative pt-32 lg:pt-40 pb-20 lg:pb-28 overflow-hidden"
    style={{ backgroundColor: colors.tealDark }}
  >
    <div
      className="absolute inset-0 opacity-[0.06] pointer-events-none"
      style={{
        background: `linear-gradient(160deg, ${colors.copper} 0%, transparent 55%)`,
      }}
    />
    <div className="max-w-[900px] mx-auto px-6 md:px-10 lg:px-16 relative text-center">
      <motion.h1
        initial={{ opacity: 0, y: 60 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease }}
        className="font-serif font-normal text-white"
        style={{
          fontSize: 'clamp(2.25rem, 5vw, 3.25rem)',
          lineHeight: 1.1,
          letterSpacing: '-0.02em',
        }}
      >
        You&apos;ve Been Invited.
      </motion.h1>
      <motion.p
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55, delay: 0.12, ease }}
        className="mt-5 font-serif text-lg sm:text-xl italic"
        style={{ color: colors.copper }}
      >
        Founding Member Pricing — Limited Availability
      </motion.p>
      <motion.p
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55, delay: 0.16, ease }}
        className="mt-8 font-sans text-base sm:text-lg leading-relaxed text-white/85"
      >
        <Link
          to="/"
          className="text-white hover:text-white/95 underline-offset-4 decoration-white/40 hover:decoration-white/70 underline transition-colors"
        >
          Hudson My Home™
        </Link>{' '}
        is opening its doors to a select group of founding members in the Des Moines metro. As a founding member, you lock in preferred pricing, receive priority service, and help shape the future of proactive home management.
      </motion.p>
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.24, ease }}
        className="mt-10 flex justify-center"
      >
        <BookingCta>Claim Your Founding Spot</BookingCta>
      </motion.div>
    </div>
  </section>
)

const BenefitsSection: React.FC = () => {
  const ref = useRef<HTMLDivElement>(null)
  const isInView = useInView(ref, { once: true, margin: '-80px' })

  return (
    <section ref={ref} className="py-20 lg:py-28" style={{ backgroundColor: colors.cream }}>
      <div className="max-w-[720px] mx-auto px-6 md:px-10 lg:px-16">
        <motion.h2
          initial={{ opacity: 0, y: 18 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.55, ease }}
          className="font-serif font-normal text-center text-grey-950 mb-12"
          style={{
            fontSize: 'clamp(1.5rem, 3vw, 2rem)',
            lineHeight: 1.15,
            letterSpacing: '-0.02em',
          }}
        >
          What founding members receive
        </motion.h2>
        <ul className="space-y-4">
          {BENEFITS.map((item, i) => (
            <motion.li
              key={item}
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7, ease, delay: 0.12 + i * 0.05 }}
              className="flex items-start gap-3.5 font-sans text-base sm:text-base leading-relaxed"
              style={{ color: colors.nearBlack }}
            >
              <Check
                className="w-5 h-5 flex-shrink-0 mt-0.5"
                style={{ color: colors.teal }}
                strokeWidth={2}
              />
              <span>{item}</span>
            </motion.li>
          ))}
        </ul>
      </div>
    </section>
  )
}

const TrustSection: React.FC = () => {
  const ref = useRef<HTMLDivElement>(null)
  const isInView = useInView(ref, { once: true, margin: '-80px' })

  return (
    <section ref={ref} className="py-20 lg:py-28 bg-white">
      <div className="max-w-[800px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="flex flex-col gap-10 text-center">
          {TRUST_ITEMS.map((text, i) => (
            <motion.p
              key={text}
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7, ease, delay: i * 0.12 }}
              className="font-sans text-base sm:text-lg leading-relaxed text-grey-800"
            >
              {text}
            </motion.p>
          ))}
        </div>
      </div>
    </section>
  )
}

const FinalCta: React.FC = () => {
  const ref = useRef<HTMLDivElement>(null)
  const isInView = useInView(ref, { once: true, margin: '-80px' })

  return (
    <section
      ref={ref}
      className="py-20 lg:py-28"
      style={{ backgroundColor: colors.tealDark }}
    >
      <div className="max-w-[640px] mx-auto px-6 md:px-10 lg:px-16 text-center">
        <motion.p
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.55, ease }}
          className="font-serif text-xl sm:text-2xl text-white"
          style={{ letterSpacing: '-0.02em' }}
        >
          Founding membership is limited.
        </motion.p>
        <motion.p
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.12, ease }}
          className="mt-4 font-sans text-base text-white/80 leading-relaxed"
        >
          Spots are reserved on a first-come, first-served basis.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.12, ease }}
          className="mt-10 flex justify-center"
        >
          <BookingCta>Book Your Founding Consultation</BookingCta>
        </motion.div>
        <motion.p
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.7, delay: 0.2, ease }}
          className="mt-6 font-sans text-sm text-white/55"
        >
          No commitment required. Cancel anytime.
        </motion.p>
        <motion.p
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.7, delay: 0.3, ease }}
          className="mt-10 font-serif italic text-base sm:text-lg"
          style={{ color: colors.copper }}
        >
          P.S. &mdash; Every home deserves a Hudson. Yours is waiting.
        </motion.p>
      </div>
    </section>
  )
}

const FoundingMemberPage: React.FC = () => (
  <div className="min-h-screen flex flex-col bg-white">
    <MetaTags
      title="Founding Member Invitation | Hudson My Home"
      description="Join as a founding member and lock in exclusive benefits. Your own Hudson, priority service, and rates that never increase."
    />
    <Navbar />
    <main className="flex-1">
      <Hero />
      <BenefitsSection />
      <TrustSection />
      <FinalCta />
    </main>
    <Footer />
  </div>
)

export default FoundingMemberPage
